import React from "react";
import { useLocation } from "react-router-dom";

import { ILesson, VideoChapter } from "../../../state";
import GuidelineSidebar from "../../../molecules/teacher/GuidelineSidebar";
import ChatBubbleIcon from "../../../atoms/ChatBubbleIcon";
import ChatBubbleModal from "../../../atoms/ChatBubbleModal";
import ChatBubblePage from "../../../pages/teacher/ChatBubblePage";
import ModalDeleteConfirm from "../../../molecules/ModalDeleteConfirm";
import { MODAL_MESSAGES } from "../../../constants/modalMessages";
import {
  CHAT_BUBBLE_MODAL_PAGES,
  ChatBubbleModalPage,
} from "../../../constants/teaching_hint";

import styles from "./Guideline.module.scss";

export interface Props {
  lesson: ILesson | null;
  teachingMaterialId: number;
  roundNo: number;
  videoChapters: VideoChapter[] | null;
  classListString: string;
  prevLessonId: number | null;
  nextLessonId: number | null;
  onChangeLesson: (lessonId: number) => void;
  surveyEnabled: boolean;
  isInitialShowChat: boolean;
  syncNotificationHint: () => void;
}

const Guideline: React.FC<Props> = ({
  lesson,
  teachingMaterialId,
  roundNo,
  videoChapters,
  classListString,
  prevLessonId,
  nextLessonId,
  onChangeLesson,
  surveyEnabled,
  isInitialShowChat,
  syncNotificationHint,
}) => {
  const [showChat, setShowChat] = React.useState(isInitialShowChat);
  const [modalPage, setModalPage] = React.useState<ChatBubbleModalPage>(
    CHAT_BUBBLE_MODAL_PAGES.SURVEY,
  );
  const closeConfirmRef = React.useRef(false);
  const [isShowPicoCloseModal, setIsShowPicoCloseModal] = React.useState(false);

  const { pathname } = useLocation();

  React.useEffect(() => {
    setModalPage(
      pathname.includes("guideline")
        ? CHAT_BUBBLE_MODAL_PAGES.HINTS
        : CHAT_BUBBLE_MODAL_PAGES.SURVEY,
    );
  }, [pathname, setModalPage]);

  const handleCloseChat = React.useCallback(() => {
    if (closeConfirmRef.current) {
      setIsShowPicoCloseModal(true);
      return;
    }
    if (isInitialShowChat) syncNotificationHint();
    setShowChat(false);
    setModalPage(CHAT_BUBBLE_MODAL_PAGES.HINTS);
  }, [isInitialShowChat, syncNotificationHint]);

  const setShouldConfirmOnClose = React.useCallback(
    (shouldConfirm: boolean) => {
      closeConfirmRef.current = shouldConfirm;
    },
    [],
  );

  const handleToggleChat = React.useCallback(() => {
    if (!showChat) {
      setShowChat(true);
      return;
    }
    handleCloseChat();
  }, [showChat, handleCloseChat]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.guidelineImages}>
        {lesson &&
          lesson.guideline_images.map((url, i) => (
            <div className={styles.imageContainer} key={i}>
              <img className={styles.image} src={url} alt={`guideline-${i}`} />
            </div>
          ))}
      </div>
      <GuidelineSidebar
        lesson={lesson}
        teachingMaterialId={teachingMaterialId}
        roundNo={roundNo}
        videoChapters={videoChapters}
        classListString={classListString}
        prevLessonId={prevLessonId}
        nextLessonId={nextLessonId}
        onChangeLesson={onChangeLesson}
      />
      {surveyEnabled && lesson?.is_retrospective === true && (
        <div className={styles.chatBubblePosition}>
          <ChatBubbleIcon onClose={handleToggleChat} modalPage={modalPage} />
          {showChat && (
            <ChatBubbleModal
              onClose={handleCloseChat}
              transparentBackDrop={true}
              className={styles.chatBubbleModalPosition}
            >
              <ChatBubblePage
                onClose={handleCloseChat}
                modalPage={modalPage}
                setModalPage={setModalPage}
                isInitialShowChat={isInitialShowChat}
                setShouldConfirmOnClose={setShouldConfirmOnClose}
              />
            </ChatBubbleModal>
          )}
          {isShowPicoCloseModal && (
            <ModalDeleteConfirm
              title={MODAL_MESSAGES.DISCARD_PICO_INPUT}
              onConfirm={() => {
                closeConfirmRef.current = false;
                setIsShowPicoCloseModal(false);
                handleCloseChat();
              }}
              onClose={() => setIsShowPicoCloseModal(false)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Guideline;
